import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { DownloadSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { supplierApi } from "@/lib/supplierPortalApi";
import { useSupplierAuth } from "@/contexts/SupplierAuthContext";
import { SupplierPortalLayout } from "./SupplierPortalLayout";
import { toast } from "@/components/ui/sonner";

const WINDOW_OPTIONS = [3, 6, 12, 24];

function Metric({ label, value, suffix = "" }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid={`scorecard-metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="text-2xl md:text-3xl font-extrabold text-[#0F172A] mt-1">{value}{suffix}</div>
    </div>
  );
}

const GRADE_COLOR = {
  A: "text-[#065F46]", B: "text-[#1E40AF]", C: "text-[#92400E]", D: "text-[#991B1B]", F: "text-[#991B1B]",
};

export default function ScorecardPage() {
  const { account } = useSupplierAuth();
  const { vendorCode } = useParams();
  const isImpersonating = !!(account?.testing_mode && vendorCode && vendorCode !== account?.vendor_code);

  const [windowMonths, setWindowMonths] = useState(6);
  const [card, setCard] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const load = async (months) => {
    setLoadError("");
    try {
      const params = { window_months: months, ...(isImpersonating ? { as_vendor: vendorCode } : {}) };
      const { data } = await supplierApi.get("/qms/scorecard", { params });
      setCard(data);
    } catch (err) {
      setLoadError(err.response?.data?.detail || "Could not load your scorecard from QMS.");
    }
  };

  useEffect(() => {
    load(windowMonths);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorCode, windowMonths]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const { data, headers } = await supplierApi.get("/qms/scorecard/pdf", { params: { window_months: windowMonths }, responseType: "blob" });
      const blob = new Blob([data], { type: headers["content-type"] || "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `scorecard_${vendorCode}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Could not download the scorecard PDF", { description: err.response?.data?.detail });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <SupplierPortalLayout active="scorecard" vendorCode={vendorCode} isImpersonating={isImpersonating} pageTitle="My Scorecard" pageSubtitle="Live from QMS">
      <div className="flex items-center justify-between gap-3 mb-4">
        <Select value={String(windowMonths)} onValueChange={(v) => setWindowMonths(Number(v))}>
          <SelectTrigger className="w-44 rounded-md" data-testid="scorecard-window-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WINDOW_OPTIONS.map((m) => (
              <SelectItem key={m} value={String(m)}>Last {m} months</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={downloadPdf} disabled={downloading} variant="outline" className="rounded-md" data-testid="scorecard-download-pdf-button">
          <DownloadSimple size={16} className="mr-1.5" /> {downloading ? "Preparing..." : "Download PDF"}
        </Button>
      </div>

      {loadError && <div className="bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B] text-sm rounded-lg px-4 py-3 mb-4" data-testid="scorecard-load-error">{loadError}</div>}

      {card && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="scorecard-metrics-row">
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 col-span-2 lg:col-span-1" data-testid="scorecard-metric-score">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Overall Score</div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#0F172A]">{card.overall_score ?? "-"}</span>
                {card.grade && <span className={`text-lg font-extrabold ${GRADE_COLOR[card.grade] || "text-slate-500"}`}>Grade {card.grade}</span>}
              </div>
            </div>
            <Metric label="Lot Rejection Rate" value={card.lrr?.value ?? "-"} suffix={card.lrr?.unit ? ` ${card.lrr.unit}` : ""} />
            <Metric label="PPM" value={card.ppm?.value ?? "-"} suffix={card.ppm?.unit ? ` ${card.ppm.unit}` : ""} />
            <Metric label="CAP Turnaround" value={card.cap_tat?.value ?? "-"} suffix={card.cap_tat?.unit ? ` ${card.cap_tat.unit}` : ""} />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3" data-testid="scorecard-metrics-row-2">
            <Metric label="Inspections" value={card.inspections_total ?? "-"} />
            <Metric label="Inspections Failed" value={card.inspections_failed ?? "-"} />
            <Metric label="Q-Notifications" value={card.qnotifs_total ?? "-"} />
            <Metric label="Qty Rejected" value={card.qty_rejected ?? "-"} />
          </div>
        </>
      )}
    </SupplierPortalLayout>
  );
}
